import React from 'react';
import classNames from 'classnames';

export type IframeInputProps = {
  id: string;
  label: string;
  inputRef: React.RefObject<HTMLDivElement>;
  isInitialized: boolean;
  isLoading?: boolean;
  loadingText?: string;
  initializingText?: string;
  error?: string;
  className?: string;
  /** Optional hint text below label. Pass empty string to reserve space without text. */
  hint?: string;
  /** Whether to always reserve space for error message. Defaults to true. */
  reserveErrorSpace?: boolean;
};

const getAriaDescribedBy = (
  isInitialized: boolean,
  hasError: boolean,
  id: string
) => {
  if (!isInitialized) return `${id}-status`;
  if (hasError) return `${id}-error`;
  return undefined;
};

/**
 * A wrapper component for Spreedly iframe inputs that mimics the styling
 * of the standard Input component, including error states.
 *
 * Since Spreedly iframes are cross-origin, we can't style the actual input.
 * This component provides consistent label, border, loading state, and error
 * display around the iframe container.
 */
export const IframeInput = ({
  id,
  label,
  inputRef,
  isInitialized,
  isLoading = false,
  loadingText = 'Loading...',
  initializingText = 'Initializing...',
  error,
  className,
  hint,
  reserveErrorSpace = true,
}: IframeInputProps) => {
  const hasError = !!error;
  const ariaDescribedBy = getAriaDescribedBy(isInitialized, hasError, id);

  return (
    <div className={classNames('flex flex-col gap-xxs', className)}>
      {/* Label */}
      <label
        htmlFor={id}
        className="font-body text-label-small font-bold text-strong"
      >
        {label}
      </label>

      {/* Hint - renders space even if empty string to maintain alignment */}
      {!!hint && (
        <span
          className={classNames(
            'font-body text-label-small',
            hint ? 'text-subtle' : 'invisible'
          )}
        >
          {hint || '\u00A0'}
        </span>
      )}

      {/* Iframe container */}
      <div
        id={id}
        ref={inputRef}
        role="group"
        aria-label={`${label} input`}
        aria-busy={!isInitialized}
        aria-invalid={hasError}
        aria-describedby={ariaDescribedBy}
        className={classNames(
          'relative flex h-12 items-center overflow-hidden rounded-xxxs border-2 px-xxs py-xs bg-regular',
          hasError ? 'border-error-foreground' : 'border-foreground-regular'
        )}
      >
        {!isInitialized && (
          <div
            id={`${id}-status`}
            className="text-body-small text-subtle"
            role="status"
            aria-live="polite"
          >
            {isLoading ? loadingText : initializingText}
          </div>
        )}
      </div>

      {/* Error message */}
      {(hasError || reserveErrorSpace) && (
        <span
          id={`${id}-error`}
          className={classNames(
            'font-body text-label-small font-bold',
            hasError ? 'text-error-foreground' : 'invisible'
          )}
          role={hasError ? 'alert' : undefined}
          aria-hidden={!hasError}
        >
          {error || '\u00A0'}
        </span>
      )}
    </div>
  );
};

export default IframeInput;
